import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Users, UserPlus, Search, Filter, Plus, X, Globe, Lock, Award, Crown, CheckCircle, Info } from 'lucide-react';

interface GroupMember {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: 'admin' | 'moderator' | 'member';
  joinedAt: Date;
  lastActive: Date;
  isOnline: boolean;
  contributionScore: number;
  articlesShared: number;
  commentsCount: number;
  likesGiven: number;
}

interface GroupDiscussion {
  id: string;
  articleId: string;
  articleTitle: string;
  articleUrl: string;
  author: GroupMember;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  likes: number;
  comments: number;
  isLiked: boolean;
  isBookmarked: boolean;
  tags: string[];
  isPinned: boolean;
  isLocked: boolean;
}

interface GroupComment {
  id: string;
  discussionId: string;
  author: GroupMember;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  likes: number;
  replies: GroupComment[];
  isLiked: boolean;
  isEdited: boolean;
}

interface ReadingGroup {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  category: string;
  tags: string[];
  privacy: 'public' | 'private' | 'invite-only';
  memberCount: number;
  maxMembers?: number;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  isJoined: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  members: GroupMember[];
  discussions: GroupDiscussion[];
  rules: string[];
  settings: {
    allowMemberInvites: boolean;
    requireApproval: boolean;
    allowAnonymousPosts: boolean;
    moderationLevel: 'none' | 'light' | 'moderate' | 'strict';
  };
}

interface ReadingGroupsProps {
  userId: string;
  onGroupJoin?: (groupId: string) => void;
  onGroupLeave?: (groupId: string) => void;
  onDiscussionCreate?: (discussion: GroupDiscussion) => void;
  onCommentCreate?: (comment: GroupComment) => void;
  className?: string;
}

const ReadingGroups: React.FC<ReadingGroupsProps> = ({
  userId,
  onGroupJoin,
  onGroupLeave,
  onDiscussionCreate,
  onCommentCreate,
  className = ''
}) => {
  const [groups, setGroups] = useState<ReadingGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<ReadingGroup | null>(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'alphabetical'>('recent');
  const [isLoading, setIsLoading] = useState(false);

  // Mock data
  useEffect(() => {
    const mockGroups: ReadingGroup[] = [
      {
        id: '1',
        name: 'Web Development Enthusiasts',
        description: 'A community of web developers sharing the latest trends, tools, and techniques in modern web development.',
        imageUrl: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=200&fit=crop',
        category: 'Web Development',
        tags: ['web-development', 'javascript', 'react', 'nodejs'],
        privacy: 'public',
        memberCount: 1247,
        maxMembers: 2000,
        createdAt: new Date('2023-06-15'),
        updatedAt: new Date('2024-01-20'),
        isActive: true,
        isJoined: true,
        isAdmin: false,
        isModerator: true,
        members: [],
        discussions: [],
        rules: [
          'Be respectful and constructive in discussions',
          'Share relevant and high-quality content',
          'No spam or self-promotion without permission',
          'Use appropriate tags for better organization'
        ],
        settings: {
          allowMemberInvites: true,
          requireApproval: false,
          allowAnonymousPosts: false,
          moderationLevel: 'moderate'
        }
      },
      {
        id: '2',
        name: 'AI & Machine Learning',
        description: 'Exploring the latest developments in artificial intelligence, machine learning, and their applications.',
        imageUrl: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=200&fit=crop',
        category: 'AI',
        tags: ['ai', 'machine-learning', 'deep-learning', 'data-science'],
        privacy: 'public',
        memberCount: 892,
        maxMembers: 1500,
        createdAt: new Date('2023-08-22'),
        updatedAt: new Date('2024-01-18'),
        isActive: true,
        isJoined: false,
        isAdmin: false,
        isModerator: false,
        members: [],
        discussions: [],
        rules: [
          'Focus on AI and ML topics',
          'Share research papers and articles',
          'Be respectful of different skill levels',
          'Provide context and explanations'
        ],
        settings: {
          allowMemberInvites: true,
          requireApproval: true,
          allowAnonymousPosts: false,
          moderationLevel: 'strict'
        }
      },
      {
        id: '3',
        name: 'Design & UX',
        description: 'A community for designers, UX researchers, and product managers to share insights and best practices.',
        imageUrl: 'https://images.unsplash.com/photo-1558655146-d09347e92766?w=400&h=200&fit=crop',
        category: 'Design',
        tags: ['design', 'ux', 'ui', 'product-design'],
        privacy: 'invite-only',
        memberCount: 456,
        maxMembers: 500,
        createdAt: new Date('2023-09-10'),
        updatedAt: new Date('2024-01-15'),
        isActive: true,
        isJoined: true,
        isAdmin: true,
        isModerator: false,
        members: [],
        discussions: [],
        rules: [
          'Share design inspiration and case studies',
          'Provide constructive feedback',
          'Respect intellectual property',
          'Use proper attribution for shared work'
        ],
        settings: {
          allowMemberInvites: false,
          requireApproval: true,
          allowAnonymousPosts: false,
          moderationLevel: 'light'
        }
      }
    ];

    setGroups(mockGroups);
  }, [userId]);

  // Handle group join/leave
  const handleGroupJoin = async (groupId: string) => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, isJoined: true, memberCount: group.memberCount + 1 }
        : group
    ));
    
    onGroupJoin?.(groupId);
    setIsLoading(false);
  };

  const handleGroupLeave = async (groupId: string) => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, isJoined: false, memberCount: group.memberCount - 1 }
        : group
    ));
    
    onGroupLeave?.(groupId);
    setIsLoading(false);
  };

  // Filter and sort groups
  const filteredGroups = groups
    .filter(group => {
      const matchesCategory = filterCategory === 'all' || group.category === filterCategory;
      const matchesSearch = searchQuery === '' || 
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case 'popular':
          return b.memberCount - a.memberCount;
        case 'alphabetical':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  // Get privacy icon
  const getPrivacyIcon = (privacy: string) => {
    switch (privacy) {
      case 'public': return <Globe className="w-4 h-4 text-green-500" />;
      case 'private': return <Lock className="w-4 h-4 text-orange-500" />;
      case 'invite-only': return <UserPlus className="w-4 h-4 text-blue-500" />;
      default: return <Globe className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get privacy color
  const getPrivacyColor = (privacy: string) => {
    switch (privacy) {
      case 'public': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'private': return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-400';
      case 'invite-only': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  return (
    <div className={`reading-groups ${className}`}>
      {/* Groups Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              Reading Groups
            </h3>
            <button
              onClick={() => setShowCreateGroup(true)}
              className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search groups..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 mb-4">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">All Categories</option>
              <option value="Web Development">Web Development</option>
              <option value="AI">AI</option>
              <option value="Design">Design</option>
              <option value="Business">Business</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="recent">Recent</option>
              <option value="popular">Popular</option>
              <option value="alphabetical">A-Z</option>
            </select>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {filteredGroups.map(group => (
              <div
                key={group.id}
                className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={() => {
                  setSelectedGroup(group);
                  setShowGroupDetails(true);
                }}
              >
                <div className="flex items-start gap-3">
                  {group.imageUrl && (
                    <img
                      src={group.imageUrl}
                      alt={group.name}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                        {group.name}
                      </h4>
                      <div className="flex items-center gap-1 ml-2">
                        {group.isAdmin && (
                          <Crown className="w-4 h-4 text-yellow-500" />
                        )}
                        {group.isModerator && (
                          <Award className="w-4 h-4 text-blue-500" />
                        )}
                        {group.isJoined && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {group.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                      <span>{group.memberCount} members</span>
                      <span>•</span>
                      <span>{group.category}</span>
                      <span>•</span>
                      <span className={`px-2 py-1 rounded-full ${getPrivacyColor(group.privacy)}`}>
                        {group.privacy}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {group.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs"
                        >
                          #{tag}
                        </span>
                      ))}
                      {group.tags.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs">
                          +{group.tags.length - 3} more
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {group.isJoined ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGroupLeave(group.id);
                          }}
                          disabled={isLoading}
                          className="px-3 py-1 bg-red-100 hover:bg-red-200 disabled:bg-gray-100 disabled:cursor-not-allowed text-red-600 rounded text-xs transition-colors"
                        >
                          Leave
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGroupJoin(group.id);
                          }}
                          disabled={isLoading}
                          className="px-3 py-1 bg-blue-100 hover:bg-blue-200 disabled:bg-gray-100 disabled:cursor-not-allowed text-blue-600 rounded text-xs transition-colors"
                        >
                          Join
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedGroup(group);
                          setShowGroupDetails(true);
                        }}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded text-xs transition-colors"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredGroups.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No groups found</p>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Group Details Modal */}
      {showGroupDetails && selectedGroup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {selectedGroup.name}
              </h3>
              <button
                onClick={() => setShowGroupDetails(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Group Info */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Description</h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {selectedGroup.description}
                </p>
              </div>
              
              {/* Group Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedGroup.memberCount}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Members</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedGroup.discussions.length}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Discussions</div>
                </div>
              </div>
              
              {/* Group Rules */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Group Rules</h4>
                <ul className="space-y-1">
                  {selectedGroup.rules.map((rule, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Group Tags */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedGroup.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadingGroups;
